import { Injectable, computed, inject, signal } from '@angular/core';
import { StorageService } from './storage.service';
import { BaseHttpService } from './base-http.service';
import { ENV_CONFIG } from '../tokens/environment.token';
import { SessionExpiredError } from '../interceptors/error.interceptor';
import { Observable, catchError, lastValueFrom, map, of, timeout } from 'rxjs';

import {
  SiteCookies,
  UserData,
  LoginPayload,
  UserInfo,
  GetSiteUserDataResponse,
  GenericApiResponse,
} from '@chefami-monorepo/types';

/**
 * AuthService
 *
 * Enhanced authentication service that handles:
 * - App initialization with user session loading
 * - Login/logout functionality
 * - User data state management with Angular signals
 * - Token management (access + refresh tokens)
 * - Reactive user data access across the application
 *
 * Usage:
 * - Use in APP_INITIALIZER for bootstrap session loading
 * - Inject anywhere to access user data reactively with signals
 * - Call login/logout methods for authentication flows
 *
 * Modern approach using Angular signals for better performance and DX.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly baseHttp = inject(BaseHttpService);
  private readonly storage = inject(StorageService);
  private readonly env = inject(ENV_CONFIG);

  // Signals for reactive state (modern approach)
  private readonly userDataSignal = signal<UserData>({
    isLoggedIn: false,
  });
  private readonly loadedSignal = signal(false);

  // Computed values from signals - these auto-update when userDataSignal changes
  readonly userData = computed(() => this.userDataSignal());
  readonly isLoaded = computed(() => this.loadedSignal());
  readonly isLoggedIn = computed(() => this.userDataSignal().isLoggedIn);
  readonly userInfo = computed(() => this.userDataSignal().userInfo);
  readonly siteInfo = computed(() => this.userDataSignal().siteInfo);

  /**
   * Fetches site data including user session from API.
   * Uses BaseHttpService for consistent HTTP handling.
   */
  private getSiteData(): Observable<GetSiteUserDataResponse> {
    const accessToken = this.storage.getCookie(SiteCookies.ACCESS_TOKEN) || '';
    const refreshToken =
      this.storage.getCookie(SiteCookies.REFRESH_TOKEN) || '';

    const payload: Record<string, string> = {
      token: accessToken,
      refresh_token: refreshToken,
    };

    // Use BaseHttpService for consistent HTTP handling
    return this.baseHttp
      .sendRequest<GetSiteUserDataResponse>(this.env.getSiteData, payload)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('[AuthService] BaseHttpService request failed:', error);
          throw error;
        }),
      );
  }

  /**
   * Loads user session and site data.
   * This should be called during app initialization (APP_INITIALIZER).
   * Updates user state and manages tokens.
   *
   * Returns a Promise for use in APP_INITIALIZER.
   */
  loadSession(): Promise<void> {
    return this.fetchSession(false);
  }

  private fetchSession(isRetry: boolean): Promise<void> {
    const data$ = this.getSiteData().pipe(
      timeout({ first: 8000 }),
      catchError((error) => {
        // Don't return null - let the error propagate or return empty response
        // Only return null for network errors, not API errors
        if (error.status === 0 || error.name === 'TimeoutError') {
          console.error('[AuthService] Network/timeout error - returning null');
          return of(null);
        }

        // For other errors, re-throw so we can see them
        console.error('[AuthService] Re-throwing error for debugging');
        throw error;
      }),
    );

    return lastValueFrom(data$)
      .then((data) => {
        if (data && data.response && data.response.code === '0000') {
          this.setUserData(data);

          // Clear tokens if user is not logged in
          if (!data.response.isLoggedIn) {
            this.clearTokens();
          }
        } else {
          console.error('[AuthService] No valid response structure:', data);
        }
      })
      .catch(async (error) => {
        // A stale or expired token isn't an outage: drop the tokens and load
        // the site data again as a logged-out visitor (once). Apps behind
        // login (account) already redirect via their session interceptor.
        if (error instanceof SessionExpiredError && !isRetry) {
          console.warn('[AuthService] Session expired - continuing logged out');
          this.clearTokens();
          await this.fetchSession(true);
          return;
        }

        console.error('[AuthService] Promise catch - Failed to load session:', {
          error,
          message: error.message,
          stack: error.stack,
        });
        if (this.env.maintainence) {
          window.location.href = this.env.maintainence;
        }
      })
      .finally(() => {
        this.loadedSignal.set(true);
      });
  }

  /**
   * Sets user data from API response.
   * Updates the signal which automatically notifies all computed values.
   */
  setUserData(responseObj: GetSiteUserDataResponse): void {
    const response = responseObj.response;

    const userData: UserData = {
      isLoggedIn: response.isLoggedIn,
      siteInfo: response.siteInfo,
      userInfo: response.userInfo,
    };

    // Update signal - this automatically updates all computed values
    this.userDataSignal.set(userData);

    // Store tokens if provided
    if (response.token) {
      this.storage.setCookie(SiteCookies.ACCESS_TOKEN, response.token);
    }
    if (response.refresh_token) {
      this.storage.setCookie(SiteCookies.REFRESH_TOKEN, response.refresh_token);
    }
  }

  /**
   * Login method for authentication
   */
  login(
    payload: LoginPayload,
    shouldSkipGlobalSpinner = false,
  ): Observable<GetSiteUserDataResponse> {
    if (!this.env.login) {
      throw new Error('Login endpoint not configured in environment');
    }
    return this.baseHttp.sendRequest<GetSiteUserDataResponse>(
      this.env.login,
      payload,
      undefined,
      undefined,
      shouldSkipGlobalSpinner, // Skip global spinner if specified
    );
  }

  /**
   * Logout method - calls API and redirects
   */
  logout(): void {
    const refreshToken = this.storage.getCookie(SiteCookies.REFRESH_TOKEN);

    if (this.env.logout && refreshToken) {
      const payload = { token: refreshToken };

      lastValueFrom(
        this.baseHttp.sendRequest<GenericApiResponse>(this.env.logout, payload),
      )
        .then(() => {
          console.warn('Logout successful');
        })
        .catch((error) => {
          console.error('Logout failed:', error);
        })
        .finally(() => {
          this.redirectToLogin();
        });
    } else {
      // If no logout endpoint or no token, just clear and redirect
      this.redirectToLogin();
    }
  }

  /**
   * Clears persisted auth state and redirects to the site (full page load).
   *
   * The user data signal is deliberately left as is: it dies with the page,
   * and clearing it would re-run every effect / toObservable that depends on
   * it (e.g. API calls) in the gap before the browser commits the navigation.
   */
  redirectToLogin(): void {
    this.clearTokens();
    this.storage.clear(true);
    this.storage.deleteCookie(SiteCookies.ORDER_PLACED);

    // replace() keeps the logged-in page out of history, so Back doesn't return to it
    const siteUrl = this.userDataSignal().siteInfo?.location_url;
    window.location.replace(siteUrl || '/');
  }

  /**
   * Updates specific user info field
   */
  updateUserInfo(key: keyof UserInfo, value: string | number): void {
    const currentData = this.userDataSignal();

    if (currentData.userInfo && (value || value === 0)) {
      const updatedUserInfo = { ...currentData.userInfo, [key]: value };
      const updatedData = { ...currentData, userInfo: updatedUserInfo };

      // Update signal - this automatically notifies all computed values
      this.userDataSignal.set(updatedData);
    }
  }

  /**
   * Gets current user data snapshot
   */
  getUserData(): UserData {
    return this.userDataSignal();
  }

  /**
   * Gets user location info
   */
  getUserLocation() {
    const siteInfo = this.userDataSignal().siteInfo;
    return {
      locationId: siteInfo?.location_id,
      locationName: siteInfo?.location_name,
    };
  }

  /**
   * Refreshes the user session
   */
  refreshSession(): Promise<void> {
    return this.loadSession();
  }

  /**
   * Clears all authentication tokens
   */
  private clearTokens(): void {
    this.storage.deleteTokens();
  }
}
